<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGlobalStore } from '@stores/global.js'
import LoadingOverlay from '@components/global/LoadingOverlay.vue'
import Scene from '@components/about/Scene.vue'
import AboutCard from '@components/about/AboutCard.vue'

gsap.registerPlugin(ScrollTrigger)

const global = useGlobalStore()
const sceneLoaded = ref(false)

const activeCard = ref(0)
const cardRefs = ref([])
let triggers = []

onMounted(() => {
	triggers = cardRefs.value.map((el, index) =>
		ScrollTrigger.create({
			trigger: el,
			start: 'top center',
			end: 'bottom center',
			// markers: true,
			onEnter: () => (activeCard.value = index),
			onEnterBack: () => (activeCard.value = index),
		}),
	)
})

onUnmounted(() => {
	triggers.forEach((t) => t.kill())
})

const cards = ref([
	{
		subtitle: 'The Brand',
		title: 'Paper Crowns',
		details: 'The birth of Paper Crowns began with a simple truth: it doesn’t take royalty or riches to wear a crown. Thus we crafted our own & raised our banner in the Humble Castle.',
		image: 'https://cdn.papercrowns.com/website/images/elements/etches/crown.png',
		rive: 'https://cdn.papercrowns.com/website/rive/about/crown.riv',
		image_alt: 'An etch of a Paper Crown.',
	},
	{
		subtitle: 'The Origin',
		title: 'Digital Masons',
		details: 'We founded Crowns in 2019, but first started working together in 2014. We were a band of dreamers turning ‘impossible quests’ into great creative work, but made waves in our scene.',
		image: 'https://cdn.papercrowns.com/website/images/elements/etches/digital-masons.png',
		rive: 'https://cdn.papercrowns.com/website/rive/about/digital-masons.riv',
		image_alt: 'An etch of 3 Digital Masons.',
	},
	{
		subtitle: 'The Growth',
		title: 'Proven Grounds',
		details: 'Over the course of a decade, we crowned brands, campaigns and products across gaming, entertainment, tech and sport. One mission at a time, we sharpened our swords and grew our offering.',
		image: 'https://cdn.papercrowns.com/website/images/elements/etches/proven-grounds.png',
		rive: 'https://cdn.papercrowns.com/website/rive/about/proven-grounds.riv',
		image_alt: 'An etch of a hammer, quill and book.',
	},
	{
		subtitle: 'The Mantra',
		title: 'Reimagine Everything',
		details: 'Today we set out to redefine conventional norms and unleash limitless imagination to craft the extraordinary. This philosophy is cast onto every brand, story & experience we build.',
		image: 'https://cdn.papercrowns.com/website/images/elements/etches/reimagine-banner.png',
		rive: 'https://cdn.papercrowns.com/website/rive/about/banner.riv',
		image_alt: 'An etch of a banner with the words "Reimagine Everything".',
	},
])
</script>

<template>
	<transition name="fade" mode="out-in">
		<LoadingOverlay v-if="!sceneLoaded && global.screenWidth > 1024" />
	</transition>

	<section class="relative flex items-center justify-center bg-crowns-off-black md:h-64">
		<div class="container">
			<div class="flex justify-center">
				<img class="md:h-44" src="https://cdn.papercrowns.com/website/images/elements/lockups/multi-award-winning-creative-marketing-agency.svg" alt="Your limitless creative solution - multi-award winning creative & marketing agency" />
			</div>
		</div>
	</section>
	<section class="relative bg-crowns-off-black bg-contain bg-no-repeat">
		<div class="absolute left-0 top-0 h-full w-full" v-if="global.screenWidth > 768">
			<div class="sticky left-0 top-0 h-dvh w-full">
				<Scene class="h-dvh" :onLoad="() => (sceneLoaded = true)" />
				<div class="absolute top-0 h-56 w-full bg-gradient-to-b from-crowns-off-black to-transparent" />
				<div class="pointer-events-none absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-crowns-off-black to-transparent" />
			</div>
		</div>
		<img class="md:hidden" src="https://cdn.papercrowns.com/website/images/backgrounds/about-castle-mobile.jpg" alt="About Castle Mobile" />
		<div class="container">
			<div class="grid-cols-3 gap-10 md:grid">
				<div class="relative z-20 col-span-1 col-start-3 -mt-24 flex flex-col gap-20 pb-10 md:gap-60 md:py-96 md:pb-32">
					<div
						v-for="(card, index) in cards"
						:key="index"
						:ref="
							(el) => {
								if (el) cardRefs[index] = el
							}
						">
						<AboutCard :card="card" :active="activeCard === index" />
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
